import { StoreData, Entity } from 'app/types-shared';
import { deepEquals } from 'app/infrastructure/js';
import { debug } from 'app/infrastructure/log';
import isEmpty from 'lodash/isempty';
import { getIds, getById } from 'app/infrastructure/store/query';

const hasProperty = (obj: object, key: string) =>
  obj && (Object.keys(obj) as any).includes(key);

/**
 * Calculates and patches local changes in userData objects, if any, onto the serverData objects
 *
 * @param userData
 * @param serverData
 * @param sent
 */
const applyLocalChanges = (
  userData: Entity,
  serverData: Entity,
  sent: Entity
) => {
  // Compare each local field value to the value that was sent off to server, ensure that a resolution row from server exists
  let changes: Entity = userData
    ? Object.keys(userData)
        .filter(prop => {
          // - Field is not present in values returned from server, and was never sent-off for save, so it must be a local change
          if (!hasProperty(serverData, prop) && !hasProperty(sent, prop)) {
            return true;
          }
          // - Given local data and sent off data, any diff is consider a local change
          return userData && sent && !deepEquals(userData[prop], sent[prop]);
        })
        .reduce(
          (res, prop) => ({
            ...res,
            [prop]: userData[prop]
          }),
          {} as Entity
        )
    : undefined;

  changes = isEmpty(changes) ? undefined : changes;

  if (changes) {
    // Assist debugging this module
    const diff = Object.keys(changes).map(c => ({
      [c]: {
        was: sent ? sent[c] : undefined,
        is: changes[c],
        // highlight missing handling of  [] compared to [{value:''}] etc, pending improvement
        diff:
          JSON.stringify(sent ? sent[c] : undefined) !==
          JSON.stringify(changes[c])
      }
    }));
    diff.length && debug('Pending user changes', diff);
  }

  // return current server values with detected local changes spreaded on top
  const result = {
    ...serverData,
    ...changes
  };

  // throw away entities without id, or props
  return isEmpty(result) || !result.id ? undefined : result;
};

/**
 * Compare store data with server request and response and return updated state
 *
 * @param {StoreData} userData current store state
 * @param {StoreData} serverData server response state
 * @param {StoreData} base server request state
 * @returns {StoreData} new store state
 */
export const getPatchedState = (
  userData: StoreData,
  serverData: StoreData,
  base: StoreData
): StoreData => {
  const ids = {
    ...getIds(userData),
    ...getIds(serverData)
  };

  return Object.keys(ids)
    .reduce(
      (res, key) => [
        ...res,
        applyLocalChanges(
          getById(userData, ids[key]),
          getById(serverData, ids[key]),
          getById(base, ids[key])
        )
      ],
      []

      //applyLocalChanges may return undefined, filter those
    )
    .filter(x => x);
};
